import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, UntypedFormBuilder } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatAccordion } from '@angular/material/expansion';
import { ActivatedRoute } from '@angular/router';
import { CommonService, NotificationService } from '@app/core/Services';
import { Select, Store } from '@ngxs/store';
import { ConfirmationBoxComponent } from '../common';
import { Location } from '@angular/common';
import {
  ConfirmationType,
  Modules,
  NotificationDetails,
  NotificationHeader,
  NotificationTextMessage,
} from '@app/core/Enum';
import {
  DefaultMail,
  GetCompanyEmailSettingsList,
  GetCompanyMailPreference,
  GetUserMailList,
  MenuState,
  PrepareGoogleOrOffice,
  SavecompanyEmailSetting,
  UpdateEmailConfig,
} from '@app/core/Store';
import { AccountProvider } from '@app/core/Enum/account-provider';
import { NgxSpinnerService } from 'ngx-spinner';
import { CompanyEmailSetting } from '@app/core/Models';
import { Observable, Subject } from 'rxjs';
import { EmailIntegrationsComponent } from '..';

@Component({
  selector: 'app-integrations',
  templateUrl: './integrations.component.html',
  styleUrls: ['./integrations.component.scss'],
})
export class IntegrationsComponent implements OnInit {
  emailIntegrationForm: FormGroup;
  accountProvider = AccountProvider;

  isExpandAll = false;
  showForm = true;
  showGmailAccounts = false;
  showOutlookAccounts = false;
  isRadioChecked = false;

  @ViewChild(MatAccordion) accordion: MatAccordion;

  userMailList: any;
  userOutlookList: any;

  moduleId = Modules.Integration;
  companyEmailSettingData: Array<CompanyEmailSetting>;
  displayGmailColumns: string[] = ['EmailId', 'Date', 'Default', 'Action'];

  @ViewChild(EmailIntegrationsComponent, { static: true })
  emailSettingDetails;

  triggerEditData: Subject<any> = new Subject<any>();

  @Select(MenuState.getSelectedMenuPermission)
  isViewPermission$: Observable<any>;
  commonNotificationText = NotificationTextMessage;
  isPermission: boolean = false;
  constructor(
    public commonService: CommonService,
    private formBuilder: UntypedFormBuilder,
    private _Activatedroute: ActivatedRoute,
    private store: Store,
    private notifier: NotificationService,
    private dialog: MatDialog,
    private spinner: NgxSpinnerService,
    private location: Location
  ) {}

  ngOnInit(): void {
    this._Activatedroute.queryParams.subscribe((params) => {
      if (Object.keys(params).length > 0) {
        params['flag'] === '1'
          ? this.notifier.success(NotificationHeader.success, params['message'])
          : this.notifier.error(NotificationHeader.error, params['message']);
      }
    });

    this.setForm();
    this.getCompanyMailPreference();
    this.getCompanyEmailSettingsList();

    this.isViewPermission$.subscribe((res) => {
      this.isPermission = res;
    });
  }

  getCompanyMailPreference(): void {
    this.store
      .dispatch(new GetCompanyMailPreference())
      .pipe()
      .subscribe((res) => {
        if (
          res.integration.integrationData !== null ||
          res.integration.integrationData !== undefined
        ) {
          this.emailIntegrationForm = this.formBuilder.group({
            isEmailSendFromCapium: [
              res.integration.integrationData ? 'true' : 'false',
            ],
          });
          this.showForm = res.integration.integrationData;

          this.getMailList();
        }
      });
  }

  getCompanyEmailSettingsList(): void {
    this.store
      .dispatch(new GetCompanyEmailSettingsList())
      .pipe()
      .subscribe((res) => {
        this.triggerEditData.next(res.integration.emailSettingData);
      });
  }

  getMailList(): void {
    if (!this.showForm) {
      //Get Mail List by default when page load
      this.getAllMail(AccountProvider.Google); // Get all Gmail
      this.getAllMail(AccountProvider.Office); // Get all Outlook
    }
  }

  setForm(): void {
    this.emailIntegrationForm = this.formBuilder.group({
      isEmailSendFromCapium: new FormControl('true'),
    });
  }

  getAllMail(providerId): void {
    this.store
      .dispatch(new GetUserMailList(providerId))
      .subscribe((res: any) => {
        if (providerId === AccountProvider.Google) {
          this.userMailList = res.integration.integrationData;
        }
        if (providerId === AccountProvider.Office) {
          this.userOutlookList = res.integration.integrationData;
        }
      });
  }

  onGmailDefaultChange(element): void {
    this.store.dispatch(new DefaultMail(element)).subscribe((res: any) => {
      if (res.integration.isDefault) {
        this.userMailList.forEach((data) => {
          data.isDefault = data.emailAddress === element.emailAddress;
        });
      }
    });
  }

  onOutlookDefaultChange(element): void {
    this.store.dispatch(new DefaultMail(element)).subscribe((res: any) => {
      if (res.integration.isDefault) {
        this.userOutlookList.forEach((data) => {
          data.isDefault = data.emailAddress === element.emailAddress;
        });
      }
    });
  }

  removeAccount(id): void {
    this.dialog
      .open(ConfirmationBoxComponent, {
        data: {
          id: id,
          type: ConfirmationType.Delete,
          moduleId: Modules.Integration,
          headerText: NotificationHeader.deleteYourAccount,
          detailText: NotificationDetails.deleteYourAccountText,
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result.integration.isDelete) {
          this.notifier.success(
            NotificationHeader.success,
            NotificationTextMessage.mailDeleteSucessfully
          );
        }
        this.getMailList();
      });
  }

  redirectToAccount(providerId): void {
    this.dialog.open(ConfirmationBoxComponent, {
      data: {
        id: [providerId],
        type: ConfirmationType.Mail,
        moduleId: Modules.Integration,
        headerText:
          providerId === AccountProvider.Google
            ? NotificationHeader.navigateMeToGmail
            : NotificationHeader.navigateMeToOutlook,
        detailText:
          providerId === AccountProvider.Google
            ? NotificationDetails.navigateMeToGmailText
            : NotificationDetails.navigateMeToOutlookText,
      },
    });
  }

  integrateGmail(type): void {
    let baseUrl = window.location.origin.toString();
    let provider = type === AccountProvider.Google ? 'google' : 'office';
    let payload = {
      ProviderId: AccountProvider.Google,
      configurationJson: '',
      emailAddress: '',
      returnUrl: baseUrl + '/manage/integrations',
      clientId: '1',
      userId: '1',
      providerType: provider,
    };

    this.store
      .dispatch(new PrepareGoogleOrOffice(payload))
      .subscribe((res: any) => {
        window.open(res.integration.integrationData, '_self');
      });
  }

  showGmailAccount() {
    this.showGmailAccounts = !this.showGmailAccounts;
  }

  showOutlookAccount() {
    this.showOutlookAccounts = !this.showOutlookAccounts;
  }

  onChange(showSection): void {
    this.isRadioChecked = true;
    this.showForm = showSection;
    this.getMailList();
  }

  toggleAccordion(): void {
    this.isExpandAll = !this.isExpandAll;
  }

  onSave(isExit: boolean): void {
    if (this.isRadioChecked) {
      this.store
        .dispatch(
          new UpdateEmailConfig(
            this.emailIntegrationForm.controls.isEmailSendFromCapium.value
          )
        )
        .pipe()
        .subscribe(
          (res) => {
            if (res) {
              if (!isExit) {
                this.onCancel();
              } else {
                this.onCancel();
                this.location.back();
              }
              this.commonService.isInitialValueChange = false;
              this.notifier.success(
                NotificationHeader.success,
                NotificationTextMessage.successMessage
              );
            } else {
              this.notifier.error(
                NotificationHeader.error,
                NotificationTextMessage.errorMessage
              );
            }
          },
          (err) => {
            this.commonService.onFailure(err.error.Message);
          }
        );
    } else if (this.emailSettingDetails.isCheckboxChecked) {
      this.emailSettingSave(isExit);
    }
  }

  emailSettingSave(isExit: boolean): void {
    if (this.dataSubmit()) {
      this.store
        .dispatch(new SavecompanyEmailSetting(this.companyEmailSettingData))
        .pipe()
        .subscribe(
          (res) => {
            if (res !== undefined) {
              if (!isExit) {
                this.onCancel();
              } else {
                this.onCancel();
                this.location.back();
              }
              this.commonService.isInitialValueChange = false;
              this.commonService.onSucess(
                NotificationTextMessage.successMessage
              );
              this.getCompanyEmailSettingsList();
            } else {
              this.commonService.onFailure(
                NotificationTextMessage.errorMessage
              );
            }
          },
          (err) => {
            this.commonService.onFailure(err.error.Message);
          }
        );
    }
  }

  dataSubmit(): boolean {
    this.companyEmailSettingData = new Array<CompanyEmailSetting>();

    try {
      this.emailSettingDetails.emailList.forEach((x) => {
        this.companyEmailSettingData.push({
          id: x.id,
          moduleId: x.moduleId,
          isActive: x.isActive,
        });
      });
    } catch (error) {
      this.spinner.hide();
      this.commonService.onFailure(NotificationTextMessage.errorMessage);
      return false;
    }
    return true;
  }

  onCancel(): void {
    this.emailSettingDetails.ngOnInit();
    this.ngOnInit();
  }
}
